import {
  Alert,
  Autocomplete,
  AutocompleteOption,
  Avatar,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from '@mui/joy';
import { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { type FormikValues } from './AddRepairCaseStepper';

import { actions } from '@/reducers/ui';
import { AppContext } from '@/shared/context/context';
import { trpc } from '@/config/trpc';

import { getAvatarInitials } from '@/shared/utils/helpers';

import useDuplicateAddressCases from '@/shared/hooks/useDuplicateAddressCases';
import useToggle from '@/shared/hooks/UseToggle';

import { BuildingIcon, InfoIcon, InlineSpinner } from '@/shared/icons/Icons';
import AddEditBuildingModal from '@/containers/Buildings/AddEditBuilding/AddEditBuildingModal';
import CaseChipDisplay from '../../CaseChipDisplay';
import HeaderStep from './HeaderStep';
import VirtualListBoxAdapter from '@/shared/components/2.0/adapters/VirtualListBoxAdapter';

const AddressStep = () => {
  const { ui, setUi } = useContext(AppContext);
  const { values, setFieldValue, errors } = useFormikContext<FormikValues>();

  const { t } = useTranslation();

  const addBuildingToggle = useToggle();

  const { data: buildings } = trpc.building.list.useQuery();
  const { data: building } = trpc.building.byId.useQuery(values.buildingId, {
    enabled: !!values.buildingId,
  });
  const { data: buildingCases } = trpc.building.cases.useQuery(values.buildingId, {
    enabled: !!values.buildingId,
  });
  const { data: countries, isLoading: countriesIsLoading } = trpc.building.address.useQuery();

  const { data: postalCodes, isLoading: postalCodesIsLoading } =
    trpc.building.postalCodes.useQuery();

  const addressToCheck = ui.case.toggles.showAddress ? values.address : building?.address ?? {};

  const { cases: duplicateAddressCases, isFetching: isFetchingDuplicateAddressCases } =
    useDuplicateAddressCases({ address: addressToCheck });

  const unlinkedDuplicateCases = useMemo(() => {
    const linkedCaseIds = new Set(buildingCases?.map(({ caseId }) => caseId));

    return duplicateAddressCases.filter(({ id }) => !linkedCaseIds.has(id));
  }, [buildingCases, duplicateAddressCases]);

  const hasAddressToCheck = !!addressToCheck.addressLine && !!addressToCheck.postalCode;

  return (
    <>
      <HeaderStep
        description={t('repairCaseForm.address.description')}
        title={t('repairCaseForm.address.title')}
        tooltipMessage={t('repairCaseForm.address.tooltip')}
      />

      <FormControl className="w-1/3">
        <Checkbox
          checked={ui.case.toggles.showAddress}
          label={t('addAddressWithoutBuilding')}
          onChange={() => {
            setUi({ type: actions.CASE_TOGGLE_SHOW_ADDRESS });
            setFieldValue('buildingId', null);
            setFieldValue('address', {
              addressLine: null,
              city: null,
              country: 'BE',
              postalCode: null,
            });
          }}
        />
      </FormControl>

      {!ui.case.toggles.showAddress && (
        <div>
          <FormControl error={!!errors.address}>
            <FormLabel required>{t('building_other')}</FormLabel>
            <div className="flex space-x-4">
              <Autocomplete
                className="grow"
                getOptionKey={(opt) => opt.id || uuidv4()}
                getOptionLabel={(opt) => opt.name || '-'}
                options={buildings || []}
                placeholder={t('typeHere')}
                startDecorator={<BuildingIcon />}
                value={buildings?.find((b) => b.id === values.buildingId) || null}
                onChange={(_e, buildingOption) => {
                  if (buildingOption) {
                    setFieldValue('buildingId', buildingOption.id);
                    setFieldValue('address', {
                      addressLine: buildingOption.address?.addressLine,
                      city: buildingOption.address?.city,
                      country: buildingOption.address?.country,
                      postalCode: buildingOption.address?.postalCode,
                    });
                  }
                }}
              />
              <IconButton onClick={() => addBuildingToggle.show()}>
                <BuildingIcon />
              </IconButton>
            </div>
          </FormControl>
        </div>
      )}

      {!!building && !ui.case.toggles.showAddress && (
        <Card className="flex flex-row items-center">
          <Avatar>{getAvatarInitials(building.name)}</Avatar>
          <div className="flex flex-col">
            <Typography level="title-lg">{t('address')}</Typography>
            <Typography>{building.name || ''} </Typography>
            <Typography>{building.address?.addressLine || ''}</Typography>
            <Typography startDecorator={building.address?.country || ''}>{`${
              building.address?.postalCode || ''
            } ${building.address?.city || ''}`}</Typography>
          </div>
        </Card>
      )}

      {!!buildingCases?.length && !ui.case.toggles.showAddress && (
        <>
          <Typography endDecorator={<Chip>{buildingCases.length}</Chip>} level="body-sm">
            {t('buildingCases')}
          </Typography>
          <div className="grid max-h-96 flex-col gap-2 overflow-auto">
            <div
              className={classNames('grid', 'max-h-96', 'flex-col', 'gap-2', 'overflow-auto', {
                'grid-cols-1': buildingCases.length === 1,
                'grid-cols-2': buildingCases.length > 3,
                'grid-cols-3': buildingCases.length > 20,
              })}
            >
              {buildingCases.map((c) => (
                <CaseChipDisplay key={c.caseId} caseEntity={{ ...c, id: c.caseId }} />
              ))}
            </div>
          </div>
        </>
      )}

      {!!building && !buildingCases?.length && (
        <Alert startDecorator={<InfoIcon />}>{t('noCasesLinked')}</Alert>
      )}

      {ui.case.toggles.showAddress && (
        <>
          <div className="flex space-x-4">
            <FormControl className="w-1/2">
              <FormLabel required>{t('addressLine')}</FormLabel>
              <Input
                placeholder={t('typeHere')}
                value={values.address.addressLine}
                onChange={(e) => setFieldValue('address.addressLine', e.target.value)}
              />
            </FormControl>
            <FormControl className="w-1/2">
              <FormLabel required>
                {t('postalCode')} / {t('city')}
              </FormLabel>

              {values.address.country === 'BE' ? (
                <Autocomplete
                  disabled={postalCodesIsLoading}
                  getOptionKey={(opt) => opt.id || uuidv4()}
                  options={postalCodes || []}
                  placeholder={t('typeHere')}
                  renderGroup={(params) => params as unknown as React.ReactNode}
                  renderOption={(props, option) => [props, option] as React.ReactNode}
                  slots={{ listbox: VirtualListBoxAdapter }}
                  startDecorator={postalCodesIsLoading && <InlineSpinner />}
                  value={postalCodes?.find(
                    (p) =>
                      p.postalCode.toString() === values.address.postalCode &&
                      p.location === values.address.city,
                  )}
                  onChange={(_e, option) => {
                    if (option) {
                      setFieldValue('address.postalCode', option.postalCode.toString());
                      setFieldValue('address.city', option.location);
                    }
                  }}
                />
              ) : (
                <div className="flex space-x-4">
                  <Input
                    type="number"
                    onChange={(e) => setFieldValue('address.postalCode', e.target.value)}
                  />
                  <Input
                    className="grow"
                    onChange={(e) => setFieldValue('address.city', e.target.value)}
                  />
                </div>
              )}
            </FormControl>
          </div>

          <FormControl>
            <FormLabel required>{t('country.label')}</FormLabel>
            <Autocomplete
              getOptionKey={(opt) => opt.code || uuidv4()}
              getOptionLabel={(opt) => opt.name || '-'}
              options={countries || []}
              placeholder={t('country.placeholder')}
              renderOption={(props, option) => {
                if (option && option.code && option.name) {
                  return (
                    <AutocompleteOption {...props}>
                      <ListItemDecorator>
                        <img
                          alt={option.name.toLowerCase()}
                          loading="lazy"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                        />
                      </ListItemDecorator>
                      <ListItemContent className="pl-2">{option.name}</ListItemContent>
                    </AutocompleteOption>
                  );
                }
                return <Alert>{t('noCountriesFound')}</Alert>;
              }}
              startDecorator={countriesIsLoading && <InlineSpinner />}
              value={countries?.find((c) => c.code === values.address.country) || null}
              onChange={(_e, country) => {
                if (country) {
                  setFieldValue('address.country', country.code);
                  setFieldValue('address.postalCode', '');
                  setFieldValue('address.city', '');
                }
              }}
            />
          </FormControl>
        </>
      )}

      {!!unlinkedDuplicateCases.length && (
        <>
          <div className="flex">
            <span className="pe-2">{t('duplicateCaseCheck.addressWarning')}</span>{' '}
            <Chip>{unlinkedDuplicateCases.length}</Chip>
          </div>
          <div className="grid max-h-96 gap-2 overflow-auto">
            <div
              className={classNames('grid', 'max-h-96', 'gap-2', 'overflow-auto', {
                'grid-cols-1': unlinkedDuplicateCases.length === 1,
                'grid-cols-2': unlinkedDuplicateCases.length > 3,
                'grid-cols-3': unlinkedDuplicateCases.length > 20,
              })}
            >
              {unlinkedDuplicateCases.map((duplicateCase) => (
                <CaseChipDisplay key={duplicateCase.id} caseEntity={duplicateCase} />
              ))}
            </div>
          </div>
        </>
      )}

      {isFetchingDuplicateAddressCases && (
        <div>
          <Typography startDecorator={<InlineSpinner />}>{t('isLoading')}</Typography>
        </div>
      )}

      {ui.case.toggles.showAddress &&
        !isFetchingDuplicateAddressCases &&
        hasAddressToCheck &&
        !unlinkedDuplicateCases.length && (
          <Alert startDecorator={<InfoIcon />}>{t('noCasesLinked')}</Alert>
        )}

      <AddEditBuildingModal toggle={addBuildingToggle} />
    </>
  );
};

export default AddressStep;
